import i18n from "../../i18n";
import { IDailySnapshot, ISnapshotProduct } from "../../@types/Dashboard/IDailySnapshot";

const sortedNames = (names: string[]) => {
  const collator = new Intl.Collator(i18n.language, { sensitivity: "base" });
  return Array.from(new Set(names.map(name => name.trim()).filter(Boolean)))
    .sort((a, b) => collator.compare(a, b) || a.localeCompare(b, i18n.language)).join(", ");
};

function groupProducts<T extends ISnapshotProduct>(items: T[], quantity: (item: T) => number) {
  const createGroup = () => ({ products: [] as string[], classifications: [] as string[], quantity: 0 });
  const groups = { normal: createGroup(), creep: createGroup() };
  items.forEach(item => {
    const group = item.creep_feeding === true ? groups.creep : groups.normal;
    if (item.trade_name) group.products.push(item.trade_name);
    if (item.classification) group.classifications.push(item.classification);
    group.quantity += quantity(item) ?? 0;
  });
  const format = (group: ReturnType<typeof createGroup>) => ({
    products: sortedNames(group.products), classifications: sortedNames(group.classifications), quantity: group.quantity,
  });
  return { normal: format(groups.normal), creep: format(groups.creep) };
}

export function dailySnapshotTrough(item: IDailySnapshot) {
  if (Array.isArray(item.supplement_trough_products)) {
    return groupProducts(item.supplement_trough_products, product => product.quantity);
  }
  const supplement = {
    products: sortedNames((item.supplement_trough_trade_name || "").split(",")),
    classifications: sortedNames((item.supplement_trough_classification || "").split(",")),
    quantity: item.supplement_trough_quantity_total ?? 0,
  };
  const empty = { products: "", classifications: "", quantity: 0 };
  return item.supplement_trough_creep_feeding === true
    ? { normal: empty, creep: supplement }
    : { normal: supplement, creep: empty };
}

export function dailySnapshotStock(item: IDailySnapshot) {
  if (Array.isArray(item.supplement_stock_products)) {
    return groupProducts(item.supplement_stock_products, product => product.stock_balance);
  }
  const fields = item as unknown as Record<string, unknown>;
  const indices = new Set(Object.keys(fields).flatMap(key => {
    const match = /^supplement_stock_(?:classification|trade_name|balance)_(\d+)$/.exec(key);
    return match ? [match[1]] : [];
  }));
  const createGroup = () => ({ products: [] as string[], classifications: [] as string[], quantity: 0 });
  const groups = { normal: createGroup(), creep: createGroup() };
  indices.forEach(index => {
    const name = fields[`supplement_stock_trade_name_${index}`];
    const classification = fields[`supplement_stock_classification_${index}`];
    const group = fields[`supplement_stock_creep_feeding_${index}`] === true ? groups.creep : groups.normal;
    if (typeof name === "string") group.products.push(name);
    if (typeof classification === "string") group.classifications.push(classification);
    group.quantity += Number(fields[`supplement_stock_balance_${index}`] ?? 0);
  });
  const formatGroup = (group: ReturnType<typeof createGroup>) => ({
    products: sortedNames(group.products),
    classifications: sortedNames(group.classifications),
    quantity: group.quantity,
  });
  return { normal: formatGroup(groups.normal), creep: formatGroup(groups.creep) };
}
